import React, { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Trophy, Users, Medal } from 'lucide-react';

const fmtCur = (v) => `AED ${Math.round(v || 0).toLocaleString()}`;
const STATUS = {
  active: { label: 'Active', cls: 'font-semibold text-foreground', badge: null },
  inactive: { label: 'Inactive', cls: 'text-muted-foreground/60', badge: 'Inactive' },
  former: { label: 'Left team', cls: 'text-muted-foreground/60', badge: 'Left team' },
};

export function FloorOverviewPanel({ periodQuery }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    apiClient.get(`/dashboard/floor-overview?${periodQuery}`).then(r => setD(r.data)).catch(() => setD(null));
  }, [periodQuery]);
  if (!d) return null;
  const mt = d.my_team;
  return (
    <div className="space-y-4" data-testid="floor-overview">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-sky-500/10 to-sky-500/5 border-sky-500/20" data-testid="floor-revenue">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />Whole floor revenue</p>
            <p className="text-2xl font-bold font-mono text-sky-600 mt-1">{fmtCur(d.floor.revenue)}</p>
            <p className="text-xs text-muted-foreground mt-1">{d.floor.deals} deals · {d.floor.teams} teams · {d.floor.agents} closers</p>
          </CardContent>
        </Card>
        {mt && (
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20" data-testid="floor-my-team">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Users className="h-3.5 w-3.5" />{mt.team || 'My team'} — floor rank</p>
              <p className="text-2xl font-bold font-mono text-primary mt-1">{mt.rank ? `#${mt.rank}` : '—'} <span className="text-sm text-muted-foreground font-sans">of {d.floor.teams}</span></p>
              <p className="text-xs text-muted-foreground mt-1">{fmtCur(mt.revenue)} · {mt.deals} deals · {mt.share_pct}% of floor</p>
            </CardContent>
          </Card>
        )}
        <Card data-testid="floor-teams">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Teams ranking</p>
            <div className="mt-1.5 space-y-0.5">
              {d.teams.slice(0, 6).map(t => (
                <div key={t.team_id} className={`flex justify-between text-xs ${t.is_my_team ? 'font-semibold text-primary' : 'text-muted-foreground'}`}>
                  <span>#{t.rank} {t.team}</span><span className="font-mono">{fmtCur(t.revenue)}</span>
                </div>))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="floor-top10">
          <CardHeader className="pb-2"><div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-yellow-500" /><CardTitle className="text-base">Floor Top 10 — all teams</CardTitle></div><CardDescription>Your team members are highlighted</CardDescription></CardHeader>
          <CardContent className="space-y-1.5">
            {d.top_agents.map(a => (
              <div key={a.agent_id} className={`flex items-center gap-3 rounded-md px-2 py-1.5 text-sm ${a.is_my_team ? 'bg-primary/10 font-semibold' : ''}`} data-testid={`floor-top-${a.rank}`}>
                <span className={`w-6 text-center font-mono text-xs ${a.rank <= 3 ? 'text-yellow-600 font-bold' : 'text-muted-foreground'}`}>{a.rank <= 3 ? <Medal className="h-4 w-4 inline" /> : a.rank}</span>
                <span className="flex-1 truncate">{a.agent}<span className="text-xs text-muted-foreground font-normal"> · {a.team || '—'}</span></span>
                <span className="text-xs text-muted-foreground">{a.deals} deals</span>
                <span className="font-mono w-28 text-right">{fmtCur(a.revenue)}</span>
              </div>))}
          </CardContent>
        </Card>

        {mt && (
          <Card data-testid="floor-my-members">
            <CardHeader className="pb-2"><div className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" /><CardTitle className="text-base">{mt.team} — revenue while in team</CardTitle></div><CardDescription>Active members in bold · former / inactive members in grey</CardDescription></CardHeader>
            <CardContent className="space-y-1.5">
              {mt.members.map(m => { const s = STATUS[m.status]; return (
                <div key={m.agent_id} className={`flex items-center gap-3 rounded-md px-2 py-1.5 text-sm ${s.cls}`} data-testid={`floor-member-${m.agent_id}`}>
                  <span className="w-8 text-center font-mono text-xs text-muted-foreground">{m.floor_rank ? `#${m.floor_rank}` : '—'}</span>
                  <span className="flex-1 truncate">{m.agent}{m.role === 'team_leader' && <span className="text-xs font-normal"> (TL)</span>}
                    {s.badge && <Badge variant="outline" className="ml-2 text-[10px] py-0 border-muted-foreground/30 text-muted-foreground/70">{s.badge}{m.status === 'former' && m.current_team ? ` → ${m.current_team}` : ''}</Badge>}
                  </span>
                  <span className="text-xs">{m.deals} deals</span>
                  <span className="font-mono w-28 text-right">{fmtCur(m.revenue)}</span>
                </div>); })}
              {mt.members.length === 0 && <p className="text-sm text-muted-foreground">No closings in this period.</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
